import { useState, useEffect, useMemo } from 'react';
import { 
  ShieldAlert, 
  Search, 
  RefreshCw, 
  ArrowUpDown, 
  AlertTriangle, 
  CheckCircle, 
  Zap, 
  Ticket,
  Settings 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { SentinelIncidentDetailModal } from './SentinelIncidentDetailModal';

export interface SentinelIncident {
  id: string;
  external_incident_id: string;
  use_case_id?: string;
  use_case_number?: string;
  title: string;
  description?: string;
  severity: string;
  status: string;
  team_id?: string;
  mitre_tactic?: string;
  mitre_technique?: string;
  assigned_engineer_name?: string;
  created_at: string;
  updated_at: string;
  sentinel_url?: string;
}

export function SentinelIncidentList() {
  const { session, user } = useAuth();
  const navigate = useNavigate();

  const userRole = (user?.role || '').toLowerCase().replace(/[-_]/g, ' ').trim();
  const isAdmin = userRole === 'super admin' || userRole === 'superadmin' || userRole === 'admin';
  const isSuperAdmin = userRole === 'super admin' || userRole === 'superadmin';

  const [incidents, setIncidents] = useState<SentinelIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncFeedback, setSyncFeedback] = useState<{ success: boolean; message: string } | null>(null);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [mitreFilter, setMitreFilter] = useState('ALL');
  const [engineerFilter, setEngineerFilter] = useState('ALL');
  const [sortField, setSortField] = useState<'created_at' | 'updated_at' | 'severity'>('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simSuccess, setSimSuccess] = useState<string | null>(null);
  const [isForbidden, setIsForbidden] = useState(false);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      setIsForbidden(false);
      const res = await fetch('/api/soc/sentinel/incidents', {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      if (res.status === 403) {
        setIsForbidden(true);
        setIncidents([]);
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setIncidents(data);
      }
    } catch (err) {
      console.error('Failed to fetch Sentinel incidents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.access_token) {
      fetchIncidents();
    }
  }, [session?.access_token]);

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setSyncFeedback(null);
      const res = await fetch('/api/soc/sentinel/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSyncFeedback({
          success: true,
          message: data.message || `Successfully synchronized ${data.synced_count} incidents.`
        });
        fetchIncidents();
      } else {
        setSyncFeedback({
          success: false,
          message: data.error?.message || data.message || 'Failed to sync incidents from Microsoft Sentinel.'
        });
      }
    } catch (err: any) {
      setSyncFeedback({
        success: false,
        message: err.message || 'Network error during Sentinel sync.'
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSimulate = async () => {
    try {
      setIsSimulating(true);
      setSimSuccess(null);
      const res = await fetch('/api/integrations/sentinel/test-incident', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({})
      });

      if (res.ok) {
        const data = await res.json();
        setSimSuccess(`Test incident ${data.external_incident_id} simulated and Step 1 Use Case ${data.use_case_number || ''} auto-created!`);
        fetchIncidents();
      }
    } catch (e: any) {
      console.error('Simulation error:', e);
    } finally {
      setIsSimulating(false);
    }
  };

  const getSeverityColor = (sev: string) => {
    const s = (sev || '').toLowerCase();
    if (s === 'critical') return 'bg-rose-500/10 text-rose-500 border-rose-500/20';
    if (s === 'high') return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
    if (s === 'low' || s === 'informational') return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
    return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
  };

  // Unique MITRE techniques for filter
  const mitreOptions = useMemo(() => {
    const set = new Set<string>();
    incidents.forEach(i => {
      if (i.mitre_technique) set.add(i.mitre_technique);
    });
    return Array.from(set);
  }, [incidents]);

  // Unique Engineers for filter
  const engineerOptions = useMemo(() => {
    const set = new Set<string>();
    incidents.forEach(i => {
      if (i.assigned_engineer_name) set.add(i.assigned_engineer_name);
    });
    return Array.from(set);
  }, [incidents]);

  // Filter and sort
  const filteredIncidents = useMemo(() => {
    return incidents
      .filter(i => {
        if (severityFilter !== 'ALL' && i.severity?.toUpperCase() !== severityFilter.toUpperCase()) return false;
        if (statusFilter !== 'ALL' && i.status?.toUpperCase() !== statusFilter.toUpperCase()) return false;
        if (mitreFilter !== 'ALL' && i.mitre_technique !== mitreFilter) return false;
        if (engineerFilter !== 'ALL' && i.assigned_engineer_name !== engineerFilter) return false;
        if (search) {
          const s = search.toLowerCase();
          return (
            i.external_incident_id.toLowerCase().includes(s) ||
            i.title.toLowerCase().includes(s) ||
            (i.use_case_number || '').toLowerCase().includes(s) ||
            (i.mitre_technique || '').toLowerCase().includes(s) ||
            (i.assigned_engineer_name || '').toLowerCase().includes(s)
          );
        }
        return true;
      })
      .sort((a, b) => {
        let valA = a[sortField] || '';
        let valB = b[sortField] || '';
        return sortAsc ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
      });
  }, [incidents, severityFilter, statusFilter, mitreFilter, engineerFilter, search, sortField, sortAsc]);

  if (isForbidden) {
    return (
      <div className="p-8 max-w-4xl mx-auto text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-rose-500/10 text-rose-500 border border-rose-500/20 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Access Restricted to SOC Team</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Microsoft Sentinel incidents and operational telemetry are strictly restricted to members of the <strong>SOC Team</strong>. Users belonging to IT Support, Development, Designing, or HR cannot access Sentinel incidents.
        </p>
        <button
          onClick={() => navigate('/admin/soc')}
          className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground hover:bg-muted border border-border text-xs font-semibold"
        >
          Return to SOC Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Microsoft Sentinel Incidents</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Real-time SIEM ingestion pipeline • SOC Team exclusive access • Automatic 15-Stage Use Case provision
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={fetchIncidents}
            disabled={loading}
            className="p-2.5 rounded-xl border border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh list"
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </button>

          <button
            type="button"
            onClick={handleSync}
            disabled={isSyncing || loading}
            className="px-3.5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-2 shadow-sm transition-all disabled:opacity-50"
            title="Poll and synchronize incidents directly from Microsoft Sentinel"
          >
            <RefreshCw className={cn("w-3.5 h-3.5", isSyncing && "animate-spin")} />
            <span>{isSyncing ? 'Syncing...' : 'Sync from Sentinel'}</span>
          </button>

          {isAdmin && (
            <button
              onClick={() => navigate('/admin/integrations/sentinel')}
              className="px-3 py-2.5 rounded-xl border border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground text-xs font-semibold flex items-center gap-1.5 transition-colors"
              title="Configure Microsoft Sentinel Integration"
            >
              <Settings className="w-3.5 h-3.5 text-indigo-400" />
              <span>Integration Settings</span>
            </button>
          )}

          {isSuperAdmin && (
            <button
              onClick={handleSimulate}
              disabled={isSimulating}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-semibold flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all disabled:opacity-50"
            >
              <Zap className="w-4 h-4" />
              {isSimulating ? 'Simulating Incident...' : 'Simulate Sentinel Incident'}
            </button>
          )}
        </div>
      </div>

      {/* Sync Feedback Alert */}
      {syncFeedback && (
        <div className={`p-4 rounded-2xl border text-xs flex items-center justify-between animate-in fade-in duration-200 ${
          syncFeedback.success 
            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
            : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
        }`}>
          <div className="flex items-center gap-2">
            {syncFeedback.success ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            <span>{syncFeedback.message}</span>
          </div>
          <button onClick={() => setSyncFeedback(null)} className="hover:text-foreground">✕</button>
        </div>
      )}

      {/* Simulation Success Alert */}
      {simSuccess && (
        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 flex items-center justify-between animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            <span>{simSuccess}</span>
          </div>
          <button onClick={() => setSimSuccess(null)} className="hover:text-foreground">✕</button>
        </div>
      )}

      {/* Filter and Search Toolbar */}
      <div className="bg-card border border-border/70 rounded-2xl p-4 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          
          {/* Search Input */}
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by Incident ID, Title, Use Case, MITRE..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full text-xs bg-background border border-border rounded-xl pl-9 pr-3 py-2 text-foreground focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Severity Filter */}
          <div>
            <select
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value)}
              className="w-full text-xs bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
            >
              <option value="ALL">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full text-xs bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
            >
              <option value="ALL">All Statuses</option>
              <option value="NEW">New</option>
              <option value="ACTIVE">Active</option>
              <option value="INVESTIGATING">Investigating</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>

          {/* MITRE Filter */}
          <div>
            <select
              value={mitreFilter}
              onChange={e => setMitreFilter(e.target.value)}
              className="w-full text-xs bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
            >
              <option value="ALL">All MITRE</option>
              {mitreOptions.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Assigned Engineer Filter */}
          <div>
            <select
              value={engineerFilter}
              onChange={e => setEngineerFilter(e.target.value)}
              className="w-full text-xs bg-background border border-border rounded-xl px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
            >
              <option value="ALL">All Engineers</option>
              {engineerOptions.map(eng => (
                <option key={eng} value={eng}>{eng}</option>
              ))}
            </select>
          </div>

        </div>

        {/* Sub-toolbar: Sort & Quick counts */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
          <div>
            Showing <strong className="text-foreground">{filteredIncidents.length}</strong> of <strong className="text-foreground">{incidents.length}</strong> incidents
          </div>

          <div className="flex items-center gap-3">
            <span>Sort by:</span>
            <button
              onClick={() => {
                if (sortField === 'created_at') setSortAsc(!sortAsc);
                else { setSortField('created_at'); setSortAsc(false); }
              }}
              className={cn(
                "flex items-center gap-1 font-medium transition-colors",
                sortField === 'created_at' ? "text-indigo-400" : "hover:text-foreground"
              )}
            >
              Created Date <ArrowUpDown className="w-3 h-3" />
            </button>
            <button
              onClick={() => {
                if (sortField === 'updated_at') setSortAsc(!sortAsc);
                else { setSortField('updated_at'); setSortAsc(false); }
              }}
              className={cn(
                "flex items-center gap-1 font-medium transition-colors",
                sortField === 'updated_at' ? "text-indigo-400" : "hover:text-foreground"
              )}
            >
              Updated Date <ArrowUpDown className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Incidents Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/30 border-b border-border text-muted-foreground font-semibold uppercase tracking-wider text-[11px]">
              <tr>
                <th className="py-3.5 px-4">Incident ID</th>
                <th className="py-3.5 px-4">Use Case ID</th>
                <th className="py-3.5 px-4">Title</th>
                <th className="py-3.5 px-4">Severity</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Team</th>
                <th className="py-3.5 px-4">MITRE Technique</th>
                <th className="py-3.5 px-4">Assigned Engineer</th>
                <th className="py-3.5 px-4">Created Date</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {loading ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-muted-foreground">
                    <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2" />
                    Loading Sentinel incidents from database...
                  </td>
                </tr>
              ) : filteredIncidents.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-muted-foreground">
                    <ShieldAlert className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                    No Microsoft Sentinel incidents found matching filter criteria.
                  </td>
                </tr>
              ) : (
                filteredIncidents.map(inc => (
                  <tr 
                    key={inc.id}
                    onClick={() => setSelectedIncidentId(inc.id)}
                    className="hover:bg-muted/30 cursor-pointer transition-colors group"
                  >
                    {/* Incident ID */}
                    <td className="py-3.5 px-4 font-mono font-bold text-indigo-400">
                      {inc.external_incident_id}
                    </td>

                    {/* Use Case ID */}
                    <td className="py-3.5 px-4">
                      {inc.use_case_number ? (
                        <span className="inline-flex items-center gap-1 font-mono font-bold px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 border border-purple-500/20">
                          <Ticket className="w-3 h-3" />
                          {inc.use_case_number}
                        </span>
                      ) : (
                        <span className="text-muted-foreground font-mono">None</span>
                      )}
                    </td>

                    {/* Title */}
                    <td className="py-3.5 px-4 font-semibold text-foreground max-w-xs truncate">
                      {inc.title}
                    </td>

                    {/* Severity */}
                    <td className="py-3.5 px-4">
                      <span className={cn("px-2.5 py-1 text-[11px] font-bold rounded-lg border inline-block", getSeverityColor(inc.severity))}>
                        {inc.severity}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4">
                      <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-secondary text-secondary-foreground border border-border">
                        {inc.status || 'Active'}
                      </span>
                    </td>

                    {/* Team */}
                    <td className="py-3.5 px-4 text-blue-400 font-medium">
                      SOC Team
                    </td>

                    {/* MITRE Technique */}
                    <td className="py-3.5 px-4 text-muted-foreground max-w-[180px] truncate" title={inc.mitre_technique}>
                      {inc.mitre_technique || 'T1190'}
                    </td>

                    {/* Assigned Engineer */}
                    <td className="py-3.5 px-4">
                      {inc.assigned_engineer_name ? (
                        <span className="font-semibold text-foreground">{inc.assigned_engineer_name}</span>
                      ) : (
                        <span className="text-muted-foreground italic">Unassigned</span>
                      )}
                    </td>

                    {/* Created Date */}
                    <td className="py-3.5 px-4 text-muted-foreground font-mono text-[11px]">
                      {new Date(inc.created_at).toLocaleDateString()} {new Date(inc.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4 text-right" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setSelectedIncidentId(inc.id)}
                        className="px-3 py-1 rounded-lg bg-secondary hover:bg-muted text-secondary-foreground border border-border text-[11px] font-semibold transition-colors"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Incident Detail Modal */}
      {selectedIncidentId && (
        <SentinelIncidentDetailModal
          incidentId={selectedIncidentId}
          onClose={() => setSelectedIncidentId(null)}
          onOpenUseCase={ucId => {
            setSelectedIncidentId(null);
            navigate(`/admin/soc?case=${ucId}`);
          }}
          onIncidentUpdated={fetchIncidents}
        />
      )}

    </div>
  );
}
